/**
 * The report dashboard's period list, derived from a company's reports.
 *
 * Pure and unit-tested, for the same reason `landing.ts` is: this decides which
 * report the dashboard renders for a given month, and getting it wrong is not
 * visible as an error — it silently shows the wrong report (or an unfinished
 * one) under the right month.
 *
 * Two things it deliberately fixes over the per-report list it replaces:
 *  - **One entry per month, not per report.** A month can hold several reports
 *    (any re-run makes one), and the period selector is a *month* picker — a
 *    per-report list offered the same month twice.
 *  - **`viewableId` is the newest COMPLETED report of the month**, separate from
 *    `latestId`/`latestStatus`, which describe the newest report whatever its
 *    state. `GET /reports/:id` answers 200 for a report that is still building,
 *    with no scores in it, so "the newest report for August" and "the report to
 *    render for August" are not the same thing while a re-run is in flight.
 */

import type { IProgressReport } from "@/types/report";

/** Report lifecycle states, as the API reports them. */
export type ReportStatus = IProgressReport["status"];

/** The fields of a report-list row this module reads. */
export interface ReportPeriodSource {
  id: number;
  status: ReportStatus;
  created_ts: string;
}

/**
 * Named for the dashboard specifically: `hooks/vb/useReportPeriods` exports its
 * own `ReportPeriod` for the Sources page dropdown, which is a different shape
 * (completed-only, no placeholder row, no latest/viewable split).
 */
export interface DashboardPeriod {
  /** Full month name as rendered in the period selector, e.g. "August". */
  month: string;
  /** Four-digit year, e.g. "2026". */
  year: string;
  /** Newest report in this month, whatever its state. Null for a month with none. */
  latestId: number | null;
  /** Status of `latestId`. Null for a month with no report — the current-month placeholder. */
  latestStatus: ReportStatus | null;
  /** Newest *completed* report in this month: the one the dashboard can render. */
  viewableId: number | null;
}

export interface DashboardPeriodsView {
  /** Newest month first. Always contains an entry for the current month. */
  periods: DashboardPeriod[];
  /** Oldest report month as `mm-yy`, for the trend chart's lower bound. */
  minStartDate: string | null;
  /** Newest report month as `mm-yy`, for the trend chart's upper bound. */
  maxReportDate: string | null;
  /**
   * The month to select when the URL names no report: the newest one with
   * something renderable, falling back to the newest month that has any report
   * at all. Prefers a renderable month over the current-month placeholder —
   * selecting the placeholder made the picker read "August" while the only
   * thing on screen was July's report. Null only for a company with no
   * reports *and* no current month, which cannot happen.
   */
  defaultPeriod: DashboardPeriod | null;
}

/** Matches a bare `YYYY-MM` period string. */
const YEAR_MONTH_ONLY = /^\d{4}-\d{2}$/;

/**
 * Normalize a date string to handle the `yyyy-mm` form by pinning it to day 10.
 *
 * Without it, '2025-11' parses as '2025-11-01T00:00:00Z', which is October 31st
 * in US timezones — i.e. the report files itself under the previous month.
 */
export const normalizeDateString = (dateString: string): string =>
  YEAR_MONTH_ONLY.test(dateString) ? `${dateString}-10` : dateString;

const toMmYy = (d: Date): string => {
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${mm}-${yy}`;
};

/** Statuses that mean "this report is still being built". */
export const isBuildingStatus = (status: ReportStatus | null): boolean =>
  status === "in_progress" || status === "finalizing";

/**
 * The (month, year) pair a report is filed under.
 *
 * Kept exactly as the dashboard has always derived it — month from the local
 * calendar, year from UTC — because this pair is both the display label and the
 * grouping key. Deriving them consistently is what matters here; changing the
 * derivation would move reports between months.
 */
const periodKeyOf = (createdTs: string): { month: string; year: string } | null => {
  const d = new Date(normalizeDateString(createdTs));
  if (Number.isNaN(d.getTime())) return null;
  return {
    month: d.toLocaleString("default", { month: "long" }),
    year: d.getUTCFullYear().toString(),
  };
};

export function buildReportPeriods(
  reports: ReportPeriodSource[],
  now: Date,
): DashboardPeriodsView {
  const byPeriod = new Map<
    string,
    { month: string; year: string; latest: ReportPeriodSource | null; viewable: ReportPeriodSource | null; order: number }
  >();

  let oldest = Number.POSITIVE_INFINITY;
  let newest = Number.NEGATIVE_INFINITY;

  for (const report of reports) {
    const key = periodKeyOf(report.created_ts);
    if (!key) continue;

    const at = new Date(normalizeDateString(report.created_ts)).getTime();
    oldest = Math.min(oldest, at);
    newest = Math.max(newest, at);

    const id = `${key.year}-${key.month}`;
    const bucket =
      byPeriod.get(id) ?? { month: key.month, year: key.year, latest: null, viewable: null, order: at };
    bucket.order = Math.max(bucket.order, at);

    const newerThan = (other: ReportPeriodSource | null) =>
      other === null || at > new Date(normalizeDateString(other.created_ts)).getTime();

    if (newerThan(bucket.latest)) bucket.latest = report;
    if (report.status === "completed" && newerThan(bucket.viewable)) bucket.viewable = report;

    byPeriod.set(id, bucket);
  }

  const periods: DashboardPeriod[] = [...byPeriod.values()]
    .sort((a, b) => b.order - a.order)
    .map((bucket) => ({
      month: bucket.month,
      year: bucket.year,
      latestId: bucket.latest?.id ?? null,
      latestStatus: bucket.latest?.status ?? null,
      viewableId: bucket.viewable?.id ?? null,
    }));

  // The current month always has a row, so the "generate this month's report"
  // prompt has something to hang off even before the first report exists.
  const currentMonth = now.toLocaleString("default", { month: "long" });
  const currentYear = now.getUTCFullYear().toString();
  if (!periods.some((p) => p.month === currentMonth && p.year === currentYear)) {
    periods.unshift({
      month: currentMonth,
      year: currentYear,
      latestId: null,
      latestStatus: null,
      viewableId: null,
    });
  }

  // The final fallback is the current month, placeholder or not. A company with
  // no reports at all still has to select *something*, or the dashboard sits on
  // a loading spinner with no selected period to resolve and never reaches its
  // own "generate your first report" prompt.
  const defaultPeriod =
    periods.find((p) => p.viewableId !== null) ??
    periods.find((p) => p.latestId !== null) ??
    periods[0] ??
    null;

  return {
    periods,
    minStartDate: Number.isFinite(oldest) ? toMmYy(new Date(oldest)) : null,
    maxReportDate: Number.isFinite(newest) ? toMmYy(new Date(newest)) : null,
    defaultPeriod,
  };
}

/**
 * The report that is still building and has nothing finished to stand in for it
 * — i.e. the whole dashboard is a progress screen. Null as soon as *any* month
 * has a renderable report, because then there is a dashboard to show instead.
 */
export function buildingReportIdOf(periods: DashboardPeriod[]): number | null {
  if (periods.some((p) => p.viewableId !== null)) return null;
  return periods.find((p) => isBuildingStatus(p.latestStatus))?.latestId ?? null;
}

/**
 * The newest report still building in ANY of this company's months, viewable
 * history or not. Drives the dashboard's auto-opened progress dialog: a run in
 * flight for the company on screen should announce itself on every visit —
 * refresh, or navigating away and back — whether or not an older report is
 * rendered behind it. (Contrast `buildingReportIdOf`, which is only the
 * "whole dashboard is a progress screen" case.)
 */
export function anyBuildingReportIdOf(periods: DashboardPeriod[]): number | null {
  return periods.find((p) => isBuildingStatus(p.latestStatus))?.latestId ?? null;
}
